import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { db } from '@/services/firebase';
import {
  newPatient as buildPatient,
  normalizeDob,
  normalizeRamqExp,
  normalizeSex,
  patientFromDoc,
  patientToMap,
  type Patient,
  type PatientStatus,
} from '@/models/patient';
import { formatAmount, parseAmountValue } from '@/lib/amount';
import { ttlTimestamp } from '@/lib/ttl';
import { normalizeRamq } from '@/lib/ramq';

const patientsCol = (uid: string) => collection(db, 'users', uid, 'patients');
const patientDoc = (uid: string, id: string) =>
  doc(db, 'users', uid, 'patients', id);

export async function addPatient(args: {
  uid: string;
  firstName?: string;
  lastName?: string;
  dob?: string;
  ramq?: string;
  ramqExp?: string;
  isNew?: boolean;
  sex?: string;
}): Promise<Patient> {
  const patient = buildPatient({ ...args, userId: args.uid });
  await setDoc(
    patientDoc(args.uid, patient.id),
    { ...patientToMap(patient), ttl: ttlTimestamp() },
    { merge: true },
  );
  return patient;
}

export async function getPatient(uid: string, id: string): Promise<Patient | null> {
  const snap = await getDoc(patientDoc(uid, id));
  return snap.exists() ? patientFromDoc(snap.id, snap.data()) : null;
}

export async function readPatientData(args: {
  uid: string;
  status: PatientStatus;
}): Promise<Patient[]> {
  const q = query(
    patientsCol(args.uid),
    where('shiftId', '==', 'current_shift'),
    where('status', '==', args.status),
    orderBy('createdAt', 'desc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => patientFromDoc(d.id, d.data()));
}

type UpdateAttr =
  | 'dob'
  | 'notes'
  | 'civ'
  | 'ramqExp'
  | 'ramq'
  | 'firstName'
  | 'lastName'
  | 'sex'
  | 'dx'
  | 'flagged'
  | 'status'
  | 'afterShiftTask';

function normalizeUpdate(
  attribute: UpdateAttr,
  value: string | boolean,
): { field: string; value: unknown } {
  switch (attribute) {
    case 'ramq':
      return { field: 'healthCard', value: normalizeRamq(String(value)) };
    case 'ramqExp':
      return { field: 'healthCardExp', value: normalizeRamqExp(String(value)) };
    case 'dob':
      return { field: 'dob', value: normalizeDob(String(value)) };
    case 'sex':
      return { field: 'sex', value: normalizeSex(String(value)) };
    default:
      return { field: attribute, value };
  }
}

export async function updatePatient(args: {
  uid: string;
  patientId: string;
  attribute: UpdateAttr;
  value: string | boolean;
}): Promise<void> {
  const { field, value } = normalizeUpdate(args.attribute, args.value);
  await updateDoc(patientDoc(args.uid, args.patientId), {
    [field]: value,
    ttl: ttlTimestamp(),
  });
}

export async function deletePatient(uid: string, id: string): Promise<void> {
  await deleteDoc(patientDoc(uid, id));
}

export async function incrementPatientTotal(args: {
  uid: string;
  patientId: string;
  delta: number;
}): Promise<void> {
  if (args.delta === 0) return;
  const snap = await getDoc(patientDoc(args.uid, args.patientId));
  if (!snap.exists()) return;
  const current = parseAmountValue(snap.data().amountBilled);
  await updateDoc(snap.ref, {
    amountBilled: formatAmount(current + args.delta),
    ttl: ttlTimestamp(),
  });
}

export async function adjustPatientTotal(args: {
  uid: string;
  patientId: string;
  oldAmount: number;
  newAmount: number;
}): Promise<void> {
  return incrementPatientTotal({
    uid: args.uid,
    patientId: args.patientId,
    delta: args.newAmount - args.oldAmount,
  });
}

export async function markPatientsOriented(args: {
  uid: string;
  patientIds: string[];
}): Promise<void> {
  if (args.patientIds.length === 0) return;
  const ttl = ttlTimestamp();
  for (let i = 0; i < args.patientIds.length; i += 450) {
    const batch = writeBatch(db);
    for (const id of args.patientIds.slice(i, i + 450)) {
      batch.update(patientDoc(args.uid, id), { status: 'oriented', ttl });
    }
    await batch.commit();
  }
}

export async function finalizePatients(args: {
  uid: string;
  finalShiftId: string;
}): Promise<void> {
  const q = query(
    patientsCol(args.uid),
    where('shiftId', '==', 'current_shift'),
  );
  const snap = await getDocs(q);
  const ttl = ttlTimestamp();
  // Firestore batch is bounded to 500 ops; chunk to be safe.
  for (let i = 0; i < snap.docs.length; i += 450) {
    const batch = writeBatch(db);
    for (const d of snap.docs.slice(i, i + 450)) {
      batch.update(d.ref, { shiftId: args.finalShiftId, ttl });
    }
    await batch.commit();
  }
}
